import { useCallback, useMemo } from 'react';
import useLocalStorage from './useLocalStorage';
import { seedRequests } from '../data/seedRequests';
import { requestStatuses } from '../data/wasteCategories';
import { generateRequestId } from '../lib/format';

const STORAGE_KEY = 'ewaste.requests.v1';

/**
 * Single source of truth for demo pickup requests, persisted to Local Storage.
 * Every consumer (form, dashboard, stats) shares the same key, so submissions
 * made on one page appear immediately on the other.
 */
export function usePickupRequests() {
  const [requests, setRequests] = useLocalStorage(STORAGE_KEY, seedRequests);

  const addRequest = useCallback(
    (values) => {
      const record = {
        ...values,
        id: generateRequestId(),
        status: requestStatuses[0],
        createdAt: new Date().toISOString(),
      };
      setRequests((prev) => [record, ...prev]);
      return record;
    },
    [setRequests]
  );

  const deleteRequest = useCallback(
    (id) => setRequests((prev) => prev.filter((r) => r.id !== id)),
    [setRequests]
  );

  /** Move a request one step along the demo lifecycle; no-op at the last stage. */
  const advanceStatus = useCallback(
    (id) =>
      setRequests((prev) =>
        prev.map((r) => {
          if (r.id !== id) return r;
          const next = requestStatuses[requestStatuses.indexOf(r.status) + 1];
          return next ? { ...r, status: next } : r;
        })
      ),
    [setRequests]
  );

  const clearRequests = useCallback(() => setRequests([]), [setRequests]);
  const resetRequests = useCallback(() => setRequests(seedRequests), [setRequests]);

  const counts = useMemo(
    () =>
      requestStatuses.reduce(
        (acc, status) => ({
          ...acc,
          [status]: requests.filter((r) => r.status === status).length,
        }),
        { total: requests.length }
      ),
    [requests]
  );

  return {
    requests,
    counts,
    addRequest,
    deleteRequest,
    advanceStatus,
    clearRequests,
    resetRequests,
  };
}

export default usePickupRequests;
